<template>
    <div id="login-page-wrapper" :class="{ 'oauth logged': isLoggedOAuth }">
        <div class="login-form-container">
            <div class="form-content">
                <img src="images/pattern-vector.svg" :alt="t('login-pattern_alt')" class="pattern-top-left" />
                <img src="images/pattern-vector.svg" :alt="t('login-pattern_alt')" class="pattern-top-right" />

                <div v-if="isAuthSuccessful" class="auth-success-container">
                    <div class="image-holder single">
                        <img width="48" height="48" src="images/kevi.svg" :alt="t('login-logo_alt')" />
                    </div>
                    <h1 class="login-title">{{ t('login-auth_granted_title') }}</h1>
                    <p class="login-subtitle" v-html="t('login-auth_granted_subtitle', [ client_name ])"></p>
                    <button class="btn btn-enter" @click="goToClientApp">
                        {{ t('login-go_to_client', [ client_name ]) }}
                    </button>
                    <span class="error" v-if="loginError" role="alert" aria-live="assertive">{{ loginError }}</span>
                </div>

                <div v-else-if="isLoggedOAuth" class="auth-success-container">
                    <div class="image-holder single">
                        <img width="48" height="48" src="images/kevi.svg" :alt="t('login-logo_alt')" />
                    </div>
                    <h1 class="login-title">{{ t('login-auth_access_title') }}</h1>
                    <p class="login-subtitle">
                        <span v-html="t('login-logged_in_as', [ user.name || user.email ])"></span>
                        <br><span v-html="t('login-authorize_access_question', [ client_name ])"></span>
                    </p>

                    <button class="btn btn-enter" @click="confirmAuthorization" :disabled="loading">
                        <div class="far fa-spinner fa-spin" v-if="loading" aria-hidden="true"></div>
                        <span v-else>{{ t('login-yes_authorize') }}</span>
                    </button>

                    <div class="login-footer">
                        <a href="#" @click.prevent="logoutAndSwitch" class="login-link">
                            {{ t('login-switch_account') }}
                        </a>
                    </div>
                    <span class="error" v-if="loginError" role="alert" aria-live="assertive">{{ loginError }}</span>
                </div>

                <password-recovery v-else-if="showRecovery" :initial-identifier="username || ''" @close="closeRecovery" />

                <form @submit.prevent v-else>
                    <div v-if="!challengeToken && !useBackupCode">
                        <div class="form-title-header">
                            <div class="image-holder">
                                <img width="32" height="40" src="images/icons/logo-mark.svg" :alt="t('login-logo_alt')" />
                            </div>
                            <div class="login-header">
                                <h1 class="login-title">{{ t('login-access_account_title') }}</h1>
                            </div>
                        </div>
                        <p class="login-subtitle">{{ t('login-access_account_subtitle') }}</p>

                        <div v-if="isOAuthFlow" class="oauth-message">
                            <p v-html="t('login-login_to_authorize', [ client_name ])"></p>
                        </div>

                        <div class="form-group">
                            <label for="login-username" class="login-label">{{ t('login-username') }}</label>
                            <input id="login-username" type="text" class="form-control" v-model="username"
                                :placeholder="t('login-username_placeholder')" autocomplete="username"
                                aria-required="true" :aria-invalid="!!loginError" @input="validateLoginInput"
                                @blur="validateLoginInput" />
                        </div>
                        <div class="form-group">
                            <label for="login-password" class="login-label">{{ t('login-password') }}</label>
                            <input id="login-password" type="password" class="form-control" v-model="password"
                                :placeholder="t('login-password_placeholder')" autocomplete="current-password"
                                aria-required="true" :aria-invalid="!!loginError" @input="validateLoginInput"
                                @blur="validateLoginInput" />
                        </div>
                        <span class="error" v-if="loginError" role="alert" aria-live="assertive">{{ loginError }}</span>
                        <button class="btn btn-enter" @click="logIn" :disabled="loading" type="submit">
                            <div class="far fa-spinner fa-spin" v-if="loading" aria-hidden="true"></div>
                            <span v-if="isOAuthFlow">{{ t('login-authorize_button') }}</span>
                            <span v-else>{{ t('login-button') }}</span>
                        </button>
                        <div class="login-footer">
                            <span>{{ t('login-forgot_password') }}</span>
                            <button type="button" class="login-link" @click="openRecovery">
                                {{ t('login-recovery-link') }}
                            </button>
                        </div>
                    </div>
                    <div v-else-if="challengeToken && !useBackupCode">
                        <div class="twofa-header">
                            <div class="image-holder single">
                                <img width="48" height="48" src="images/kevi.svg" :alt="t('login-logo_alt')" />
                            </div>
                            <h1 class="twofa-title">{{ t('login-twofa-title') }}</h1>
                            <p class="twofa-subtitle">{{ t('login-twofa-subtitle') }}</p>
                        </div>

                        <div class="twofa-code-container">
                            <div class="code-inputs">
                                <input type="text" id="twofa-code-0" name="twofa-code-0" maxlength="1"
                                    class="code-input" @input="handleCodeInput" @keydown="handleKeyDown"
                                    @paste="handleTwofaPaste" data-index="0" :placeholder="twofaPlaceholders[0]" />
                                <input type="text" id="twofa-code-1" name="twofa-code-1" maxlength="1"
                                    class="code-input" @input="handleCodeInput" @keydown="handleKeyDown"
                                    @paste="handleTwofaPaste" data-index="1" :placeholder="twofaPlaceholders[1]" />
                                <input type="text" id="twofa-code-2" name="twofa-code-2" maxlength="1"
                                    class="code-input" @input="handleCodeInput" @keydown="handleKeyDown"
                                    @paste="handleTwofaPaste" data-index="2" :placeholder="twofaPlaceholders[2]" />
                                <input type="text" id="twofa-code-3" name="twofa-code-3" maxlength="1"
                                    class="code-input" @input="handleCodeInput" @keydown="handleKeyDown"
                                    @paste="handleTwofaPaste" data-index="3" :placeholder="twofaPlaceholders[3]" />
                                <input type="text" id="twofa-code-4" name="twofa-code-4" maxlength="1"
                                    class="code-input" @input="handleCodeInput" @keydown="handleKeyDown"
                                    @paste="handleTwofaPaste" data-index="4" :placeholder="twofaPlaceholders[4]" />
                                <input type="text" id="twofa-code-5" name="twofa-code-5" maxlength="1"
                                    class="code-input" @input="handleCodeInput" @keydown="handleKeyDown"
                                    @paste="handleTwofaPaste" data-index="5" :placeholder="twofaPlaceholders[5]" />
                            </div>
                        </div>

                        <span class="error" v-if="twofaError" role="alert" aria-live="assertive">{{ twofaError }}</span>

                        <button class="btn btn-verify" @click="userCodeVerify" :disabled="loading || !userCode || userCode.length !== 6" type="submit">
                            <div class="far fa-spinner fa-spin" v-if="loading" aria-hidden="true"></div>
                            {{ t('login-twofa-verify_button') }}
                        </button>

                        <div class="backup-link">
                            <p>{{ t('login-twofa-use_backup') }}&nbsp;<span @click="useBackupCode = true"
                                    class="backup-code-link">{{ t('login-twofa-click_here') }}</span></p>
                        </div>
                    </div>
                    <div v-else>
                        <div class="backup-header">
                            <div class="image-holder single">
                                <img width="48" height="48" src="images/kevi.svg" :alt="t('login-logo_alt')" />
                            </div>
                            <h1 class="backup-title">{{ t('login-backup-title') }}</h1>
                            <p class="backup-subtitle">{{ t('login-backup-subtitle') }}</p>
                        </div>

                        <div class="backup-code-container">
                            <div class="code-inputs">
                                <input type="text" id="backup-code-0" name="backup-code-0" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="0" :placeholder="backupPlaceholders[0]" />
                                <input type="text" id="backup-code-1" name="backup-code-1" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="1" :placeholder="backupPlaceholders[1]" />
                                <input type="text" id="backup-code-2" name="backup-code-2" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="2" :placeholder="backupPlaceholders[2]" />
                                <input type="text" id="backup-code-3" name="backup-code-3" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="3" :placeholder="backupPlaceholders[3]" />
                                <input type="text" id="backup-code-4" name="backup-code-4" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="4" :placeholder="backupPlaceholders[4]" />
                                <input type="text" id="backup-code-5" name="backup-code-5" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="5" :placeholder="backupPlaceholders[5]" />
                                <input type="text" id="backup-code-6" name="backup-code-6" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="6" :placeholder="backupPlaceholders[6]" />
                                <input type="text" id="backup-code-7" name="backup-code-7" maxlength="1"
                                    class="code-input" @input="handleBackupCodeInput" @keydown="handleBackupKeyDown"
                                    @paste="handleBackupPaste" data-index="7" :placeholder="backupPlaceholders[7]" />
                            </div>
                        </div>

                        <span class="error" v-if="backupError" role="alert" aria-live="assertive">{{ backupError
                        }}</span>

                        <button class="btn btn-verify" @click="userBackupCodeVerify" :disabled="loading || !backupCode || backupCode.length !== 8" type="submit">
                            <div class="far fa-spinner fa-spin" v-if="loading" aria-hidden="true"></div>
                            {{ t('login-twofa-verify_button') }}
                        </button>

                        <div class="backup-link">
                            <p>
                                {{ t('login-backup-use_authenticator') }}
                                <span class="backup-code-link" @click="useBackupCode = false">
                                    {{ t('login-backup-code_link') }}
                                </span>
                            </p>
                        </div>
                    </div>
                </form>
                <img src="images/pattern-vector.svg" :alt="t('login-pattern_alt')" class="pattern-bottom-right" />
                <img src="images/pattern-vector.svg" :alt="t('login-pattern_alt')" class="pattern-bottom-left" />
            </div>
        </div>

        <div v-if="!isLoggedOAuth" class="login-promo-container">
            <img src="/images/login-right-screen.png" :alt="t('login-promo_alt')" class="promo-image" />
        </div>
    </div>
</template>

<script>
import keviApi from "./../keviApi";
import PasswordRecovery from "./password-recovery";
export default {
    props: {
        user: Object,
    },
    components: {
        PasswordRecovery,
    },
    data: function () {
        return {
            ready: true,
            loading: false,
            loginUrl: null,
            loginError: "",
            twofaError: "",
            backupError: "",
            authBody: null,
            username: null,
            password: null,
            userCode: null,
            backupCode: null,
            challengeToken: null,
            useBackupCode: false,
            redirect_uri: null,
            client_name: null,
            isOAuthFlow: false,
            oAuthAcessCode: undefined,
            redirectToFallback: false,
            isAuthSuccessful: false,
            showRecovery: false,
        };
    },
    created: function () {
        document.getElementsByTagName("title")[ 0 ].innerText = this.t("login-page_title");

        const query = this.$route.query;
        if (query.redirect_uri && query.client_name) {
            try {
                new URL(query.redirect_uri);
                this.redirect_uri = query.redirect_uri;
                this.client_name = query.client_name;
                this.isOAuthFlow = true;
            } catch (error) {
                console.error("Invalid redirect_uri provided:", query.redirect_uri);
                this.loginError = this.t("login-invalid_redirect_uri");
            }
        }
        if (this.isRecoveryQuery(query.recovery)) {
            this.showRecovery = true;
        }
    },
    computed: {
        isLoggedOAuth() {
            return this.isOAuthFlow && this.user && this.user.token;
        },
        twofaPlaceholders() {
            return ["1", "2", "3", "4", "5", "6"];
        },
        backupPlaceholders() {
            return ["A", "B", "C", "D", "1", "2", "3", "4"];
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        isRecoveryQuery(value) {
            return value === "1" || value === "true" || value === true;
        },
        openRecovery() {
            this.showRecovery = true;
            if (!this.isRecoveryQuery(this.$route.query.recovery)) {
                this.$router.replace({
                    path: this.$route.path,
                    query: Object.assign({}, this.$route.query, { recovery: "1" }),
                }).catch(() => {});
            }
        },
        closeRecovery() {
            this.showRecovery = false;
            if (this.$route.query.recovery == null) return;
            const query = Object.assign({}, this.$route.query);
            delete query.recovery;
            this.$router.replace({ path: this.$route.path, query }).catch(() => {});
        },
        confirmAuthorization: function () {
            this.loading = true;
            this.loginError = "";

            const options = keviApi.getOptions("auth/code", "POST");

            this.$http(options).then(
                function (response) {
                    this.loading = false;
                    if (response.status === 200 && response.body.access_code) {
                        this.oAuthAcessCode = response.body.access_code;
                        this.username = response.body.username;
                        this.isAuthSuccessful = true;
                    } else {
                        this.loginError = this.t("login-auth_code_generation_failed");
                    }
                },
                function (response) {
                    this.loading = false;
                    if (response.status === 401) {
                        this.loginError = this.t("login-session_expired");
                        this.$store.dispatch("logout");
                    } else {
                        // i18n-validator-ignore-next-line: backend message fallback
                        this.loginError = response.body.message || this.t("login-server_connection_error");
                    }
                }
            );
        },
        // ---- NOVO MÉTODO: Trocar de conta (Logout) ----
        logoutAndSwitch: function () {
            this.$store.dispatch("logout");
            this.username = "";
            this.password = "";
        },
        logIn: function () {
            this.loading = true;
            this.loginError = "";
            var user = this.username;
            var password = this.password;
            var authUrl = keviApi.getApiEndpoint("auth");

            var requestBody = {};
            if (this.isOAuthFlow) {
                requestBody.oauth = true;
            } else {
                requestBody.product = "marketing";
            }

            var options = {
                url: authUrl,
                method: "POST",
                body: requestBody,
                headers: {
                    Authorization: "Basic " + btoa(user + ":" + password),
                },
            };
            this.$http(options).then(
                function (response) {
                    this.loading = false;
                    if (response.status !== 200) {
                        this.loginError = this.$root.t("login-error");
                        return;
                    }
                    if (response.body.status === "twofa_required" && response.body.challenge_token) {
                        // A sessão só existe depois do código: nada é gravado aqui.
                        this.challengeToken = response.body.challenge_token;
                        this.useBackupCode = false;
                        return;
                    }
                    this.handleAuthenticated(response.body);
                },
                function () {
                    this.loading = false;
                    this.loginError = this.$root.t("login-error");
                },
            );
        },
        handleAuthenticated: function (body) {
            this.authBody = body;
            if (this.isOAuthFlow) {
                if (body.access_code) {
                    this.oAuthAcessCode = body.access_code;
                    this.redirectToFallback = true;
                } else {
                    this.loginError = this.t("login-oauth_access_code_missing");
                }
                return;
            }
            if (body.token) {
                this.$store.dispatch("login", body);
            } else {
                this.loginError = this.$root.t("login-error");
            }
        },
        backToPassword: function (message) {
            this.challengeToken = null;
            this.useBackupCode = false;
            this.userCode = null;
            this.backupCode = null;
            this.password = "";
            this.loginError = message || "";
        },
        verifyChallenge: function (payload, setError) {
            this.loading = true;
            setError("");
            const options = {
                url: keviApi.getApiEndpoint("auth/2fa"),
                method: "POST",
                body: Object.assign({ challenge_token: this.challengeToken }, payload),
            };
            this.$http(options).then(
                function (response) {
                    this.loading = false;
                    if (response.status === 200 && response.body.token) {
                        this.challengeToken = null;
                        this.handleAuthenticated(response.body);
                    } else {
                        setError(this.t("login-twofa-error_invalid"));
                    }
                },
                function (response) {
                    this.loading = false;
                    const message = response.body && response.body.message;
                    if (message === "too_many_attempts") {
                        this.backToPassword(this.t("login-twofa-error_too_many"));
                    } else if (message === "expired") {
                        this.backToPassword(this.t("login-twofa-error_expired"));
                    } else if (message === "code_used") {
                        setError(this.t("login-twofa-error_code_used"));
                    } else if (message === "invalid_code") {
                        setError(this.t("login-twofa-error_invalid"));
                    } else {
                        setError(this.t("login-server_connection_error"));
                    }
                },
            );
        },
        userCodeVerify: function () {
            if (!this.userCode || this.userCode.length !== 6) return;
            this.verifyChallenge({ code: this.userCode }, (msg) => { this.twofaError = msg; });
        },
        userBackupCodeVerify: function () {
            if (!this.backupCode || this.backupCode.length !== 8) return;
            const code = this.backupCode.substring(0, 4) + "-" + this.backupCode.substring(4, 8);
            this.verifyChallenge({ backup_code: code }, (msg) => { this.backupError = msg; });
        },
        handleTwofaPaste(event) {
            event.preventDefault();
            const pastedText = (event.clipboardData || window.clipboardData).getData("text");
            const cleanText = pastedText.replace(/[^0-9]/g, "");
            if (cleanText.length === 0) {
                return;
            }
            this.clearCodeInputs("twofa");
            const maxLength = Math.min(cleanText.length, 6);
            for (let i = 0; i < maxLength; i++) {
                const input = document.querySelector(`input[data-index="${i}"]`);
                if (input && input.closest(".twofa-code-container")) {
                    input.value = cleanText[ i ];
                    input.classList.remove("error");
                }
            }
            this.userCode = cleanText.substring(0, 6);
            const focusIndex = Math.min(maxLength, 5);
            const focusInput = document.querySelector(`input[data-index="${focusIndex}"]`);
            if (focusInput && focusInput.closest(".twofa-code-container")) {
                focusInput.focus();
            }
        },
        redirect: function () {
            if (this.$route.query.redirect) {
                this.$router.push(this.$route.query.redirect);
            } else {
                this.$router.push("/dashboard");
            }
        },
        handleCodeInput: function (event) {
            const input = event.target;
            const value = event.target.value;
            const index = parseInt(input.getAttribute("data-index"));

            input.classList.remove("error");

            if (!/^\d$/.test(value)) {
                input.value = "";
                if (value !== "") {
                    input.classList.add("error");
                }
                return;
            }

            let codeArray = this.userCode ? this.userCode.split("") : [ "", "", "", "", "", "" ];
            codeArray[ index ] = value;
            this.userCode = codeArray.join("");

            if (value && index < 5) {
                const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
                if (nextInput) {
                    nextInput.focus();
                }
            }
        },
        handleKeyDown: function (event) {
            const input = event.target;
            const index = parseInt(input.getAttribute("data-index"));

            if (event.key === "Backspace" && !input.value && index > 0) {
                const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
                if (prevInput) {
                    prevInput.focus();
                }
            }
        },
        handleBackupCodeInput: function (event) {
            const input = event.target;
            const value = event.target.value;
            const index = parseInt(input.getAttribute("data-index"));

            input.classList.remove("error");

            if (!/^[a-zA-Z0-9]$/.test(value)) {
                input.value = "";
                if (value !== "") {
                    input.classList.add("error");
                }
                return;
            }

            const upperValue = value.toUpperCase();
            input.value = upperValue;

            let codeArray = this.backupCode ? this.backupCode.split("") : [ "", "", "", "", "", "", "", "" ];
            codeArray[ index ] = upperValue;
            this.backupCode = codeArray.join("");

            if (upperValue && index < 7) {
                const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
                if (nextInput) {
                    nextInput.focus();
                }
            }
        },
        handleBackupKeyDown: function (event) {
            const input = event.target;
            const index = parseInt(input.getAttribute("data-index"));

            if (event.key === "Backspace" && !input.value && index > 0) {
                const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
                if (prevInput) {
                    prevInput.focus();
                }
            }
        },
        handleBackupPaste: function (event) {
            event.preventDefault();

            const pastedText = (event.clipboardData || window.clipboardData).getData("text");

            const cleanText = pastedText.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

            if (cleanText.length === 0) {
                return;
            }

            this.clearCodeInputs("backup");

            const maxLength = Math.min(cleanText.length, 8);
            for (let i = 0; i < maxLength; i++) {
                const input = document.querySelector(`input[data-index="${i}"]`);
                if (input && input.closest(".backup-code-container")) {
                    input.value = cleanText[ i ];
                    input.classList.remove("error");
                }
            }

            this.backupCode = cleanText.substring(0, 8);

            const focusIndex = Math.min(maxLength, 7);
            const focusInput = document.querySelector(`input[data-index="${focusIndex}"]`);
            if (focusInput && focusInput.closest(".backup-code-container")) {
                focusInput.focus();
            }
        },
        clearCodeInputs: function (section) {
            const maxInputs = section === "backup" ? 8 : 6;
            for (let i = 0; i < maxInputs; i++) {
                const input = document.querySelector(`input[data-index="${i}"]`);
                if (input) {
                    input.value = "";
                    input.classList.remove("error");
                }
            }
        },
        validateLoginInput: function (event) {
            const input = event.target;
            const value = input.value.trim();

            input.classList.remove("filled", "error");

            if (value) {
                input.classList.add("filled");
            } else if (event.type === "blur" || (event.type === "input" && event.target.value !== value)) {
                input.classList.add("error");
            }
        },
        goToClientApp: function () {
            if (!this.redirect_uri || !this.oAuthAcessCode) {
                this.loginError = this.t("login-redirect_missing_info");
                return;
            }
            this.loginError = "";

            const redirectUrl = new URL(this.redirect_uri);
            redirectUrl.searchParams.append('access_code', this.oAuthAcessCode);
            redirectUrl.searchParams.append('username', this.username);

            const newWindow = window.open(redirectUrl.toString(), "_blank");

            if (!newWindow) {
                this.loginError = this.t("login-popup_blocked");
            }

            if (this.authBody) {
                this.$store.dispatch("login", this.authBody);
            }
        },
    },
    watch: {
        "$route.query.recovery"(value) {
            const open = this.isRecoveryQuery(value);
            if (open !== this.showRecovery) {
                this.showRecovery = open;
            }
        },
        redirectToFallback: function (newVal) {
            if (newVal && this.isOAuthFlow) {
                this.isAuthSuccessful = true;
            }
        },
        user: function () {
            if (this.user.token && !this.isOAuthFlow) {
                var _self = this;
                window.setTimeout(function () {
                    _self.redirect();
                }, 200);
            }
        },
        useBackupCode: function (newVal) {
            if (newVal) {
                this.userCode = null;
                this.clearCodeInputs("twofa");
            } else {
                this.backupCode = null;
                this.clearCodeInputs("backup");
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            const usernameInput = document.getElementById("login-username");
            const passwordInput = document.getElementById("login-password");

            if (usernameInput && this.username) {
                usernameInput.classList.add("filled");
            }
            if (passwordInput && this.password) {
                passwordInput.classList.add("filled");
            }
        });
    },
};
</script>

<style lang="css" scoped>
#login-page-wrapper {
    display: flex;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
}

.login-form-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #FFFFFF;
    position: relative;
}


#login-page-wrapper.oauth.logged {
    width: 100%;
    height: calc(100dvh - 80px);
}

.oauth.logged .login-form-container {
    background-color: transparent;
}

.oauth.logged :is(.pattern-top-left, .pattern-top-right, .pattern-bottom-left, .pattern-bottom-right) {
    display: none;
}

.login-promo-container {
    flex: 1;
    background-color: black;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
}

.promo-image {
    margin-bottom: -200px;
    margin-right: -200px;
    max-width: calc(100% + 160px);
    max-height: calc(100% + 160px);
    object-fit: cover;
}

.form-content {
    width: 100%;
    max-width: 333px;
}

.form-content:has(.password-recovery),
.form-content:has(.twofa-header),
.form-content:has(.backup-header) {
    max-width: 523px;
}

.pattern-top-left {
    position: absolute;
    top: 24px;
    left: 24px;
    width: 32px;
    height: 32px;
}

.pattern-top-right {
    position: absolute;
    top: 32px;
    right: 32px;
    width: 32px;
    height: 32px;
    rotate: 90deg;
    display: none;
}

.pattern-bottom-left {
    position: absolute;
    bottom: 24px;
    left: 24px;
    width: 32px;
    height: 32px;
    transform: rotate(-90deg);
}

.pattern-bottom-right {
    position: absolute;
    bottom: 32px;
    right: 32px;
    width: 32px;
    height: 32px;
    transform: rotate(-180deg);
    display: none;
}

.form-title-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
}
.form-title-header .image-holder img {
    width: 32px;
    height: 40px;
}

.image-holder.single {
    margin-bottom: 24px;
}

.login-header {
    text-align: left;
    margin-bottom: 0;
}

.twofa-header {
    text-align: center;
    margin-bottom: 32px;
}

.login-title,
.twofa-title,
.backup-title {
    text-align: center;
    color: #2aaa4c;
    font-family: "Sora", sans-serif;
    font-size: 32px;
    font-weight: 500;
    line-height: 1.2;
    margin: 0;
}

.login-subtitle,
.twofa-subtitle,
.backup-subtitle {
    color: #656565;
    font-family: "Sora", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    width: 304px;
    margin: 16px auto 24px;
    text-align: center;
}

.oauth-message {
    background-color: #F3F4F6;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 24px;
    text-align: center;
}

.oauth-message p {
    margin: 0;
    color: #4B5563;
    font-size: 0.9rem;
}

.oauth-message strong {
    color: #1F2937;
    font-weight: 600;
}


.backup-header {
    text-align: center;
    margin-bottom: 32px;
}

.backup-code-container {
    margin: 32px 0;
}

.twofa-code-container,
.backup-code-container {
    margin: 32px 0;
}

.code-inputs {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.code-input {
    width: 48px;
    height: 48px;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    text-align: center;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1F2937;
    background-color: #F9FAFB;
    outline: none;
    transition: all 0.2s ease;
}

.code-input::placeholder {
    color: #9CA3AF;
    font-weight: 400;
}

.code-input:focus {
    border-color: var(--secondary);
    background-color: #FFFFFF;
    box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.3);
}

.code-input:not(:placeholder-shown) {
    border-color: var(--secondary);
    background-color: #F9FAFB;
}

.code-input.error {
    border-color: #EF4444;
    background-color: #FEF2F2;
}

.code-input.error:focus {
    border-color: #EF4444;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.3);
}

.btn-verify {
    width: 100%;
    height: 48px;
    border-radius: 8px;
    margin: 24px 0;
    color: #fff;
    background-color: var(--secondary);
    border: none;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 8px;
}

.btn-verify:hover {
    background-color: #1e7431;
}

.btn-verify:disabled {
    background-color: #9cd8ac;
    cursor: not-allowed;
}

.backup-link {
    text-align: center;
    margin-top: 24px;
}

.backup-link p {
    color: #6B7280;
    font-size: 0.9rem;
    margin: 0;
}

.backup-code-link {
    color: var(--secondary);
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
}

.form-group {
    width: 330px;
    margin: 24px auto 0;
}

.login-label {
    font-family: "Inter", sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.2;
    color: #383c50;
    display: block;
    margin-bottom: 8px;
}

.form-control {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    border: 1px solid #e5e6eb;
    padding: 0 12px;
    background-color: #f9f9fb;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #1f2337;
    box-shadow: none;
    margin: 0;
}
.form-control::placeholder {
    color: #9598a6;
    font-size: 12px;
}

.form-control:focus {
    outline: none;
    border-color: var(--secondary);
    box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.3);
    background-color: #FFFFFF;
}

.form-control.filled {
    border-color: var(--secondary);
    background-color: #F9FAFB;
}

.form-control.error {
    border-color: #EF4444;
    background-color: #FEF2F2;
}

.form-control.error:focus {
    border-color: #EF4444;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.3);
}

.btn-enter {
    width: 333px;
    display: block;
    margin: 24px auto 0;
    padding: 12px;
    border-radius: 8px;
    color: #f1fcf3;
    background-color: #2aaa4c;
    border: none;
    font-family: "Inter", sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.2;
    cursor: pointer;
}

.btn-enter:hover {
    color: #fff;
    background-color: #1e7431;
}

.btn-enter:disabled {
    background-color: #9cd8ac;
    cursor: not-allowed;
}

.login-footer {
    text-align: center;
    margin-top: 24px;
    font-family: "Sora", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    color: #000000;
}

.login-link {
    color: #2aaa4c;
    font-weight: 400;
    text-decoration: underline;
    margin-left: 4px;
    background: none;
    border: 0;
    padding: 0;
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
}

button.login-link {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.error[role="alert"] {
    color: #e74c3c;
    font-size: 0.9em;
    margin-top: 4px;
    display: block;
}

.auth-success-container {
    text-align: center;
}

.auth-success-container .login-subtitle {
    margin-bottom: 24px;
}

.auth-success-container .btn-enter {
    margin-top: 0;
}

.auth-success-container strong {
    color: #1F2937;
    font-weight: 600;
}

@media (max-width: 992px) {
    #login-page-wrapper {
        flex-direction: column;
    }

    .login-promo-container {
        display: none;
    }

    .pattern-bottom-right {
        display: block;
    }

    .pattern-top-right {
        display: block;
    }

    .login-form-container {
        padding: 20px;
    }
}

@media (max-width: 768px) {
    .login-form-container {
        padding: 16px;
    }

    .login-title {
        font-size: 1.75rem;
    }
}
</style>
